import React, { useEffect } from 'react';
import { ModalDrawer, useModal } from '@blueprint/ui';

import { trackPromptExperiment } from './AuthPrompts.tracking';
import {
    PromptContent,
    PromptHeading,
    RegisterButton,
    SignInButton
} from './AuthPrompts.styles';
import { AuthPromptsProps } from './AuthPrompts.types';
import { withOptimizely, WithOptimizelyProps } from 'shared/optimizely';
import useIsWidthLessThanBreakpoint from 'shared/hooks/app/useIsWidthLessThanBreakpoint';
import {
    CONSENT_COOKIE,
    PROMPTS,
    PROMPT_EXPERIMENT_BREAKPOINT
} from './AuthPrompts.constants';
import { useUser } from 'shared/hooks/useUser';
import Cookies from 'js-cookie';
import useExperiment from '../AuthModal/hooks/useExperiment';
import useDelay from './utils/useDelay';

const AuthPrompts = ({
    promptType,
    signIn,
    register,
    journey,
    optimizely
}: AuthPromptsProps & WithOptimizelyProps) => {
    const [
        isShowingAuthPrompt,
        openAuthPromptModal,
        closeAuthPromptModal
    ] = useModal();

    const [isMobile] = useIsWidthLessThanBreakpoint([
        PROMPT_EXPERIMENT_BREAKPOINT
    ]);
    const user = useUser();

    const experimentName = PROMPTS[promptType].experimentName;
    const { optimizelyVariation } = useExperiment(
        optimizely,
        PROMPTS[promptType].experimentName
    );

    const { showAfterDelay } = useDelay(
        PROMPTS[promptType]?.delayCondition?.[optimizelyVariation || 'prompt']
            ?.time
    );

    const isFeatureEnabled = optimizelyVariation !== null;
    const isLoggedOut = user.loggedIn === false;
    const hasFulfilledCondition =
        PROMPTS[promptType].condition(
            isMobile,
            isLoggedOut,
            Cookies.get(CONSENT_COOKIE)
        ) &&
        sessionStorage.getItem(`authPromptClose_${experimentName}`) === null;

    useEffect(() => {
        trackPromptExperiment(experimentName, optimizelyVariation);

        if (isFeatureEnabled && user.loading === false) {
            if (hasFulfilledCondition && showAfterDelay) {
                PROMPTS[promptType].tracking.trackOpen(journey);
                openAuthPromptModal(null);
            }
        }
    }, [isMobile, user, showAfterDelay]);

    const handleRegisterClick = () => {
        PROMPTS[promptType].tracking.trackRegister(journey);
        sessionStorage.setItem(`authPromptClose_${experimentName}`, '1');
        closeAuthPromptModal();
        register();
    };

    const handleSignInClick = () => {
        PROMPTS[promptType].tracking.trackSignIn(journey);
        sessionStorage.setItem(`authPromptClose_${experimentName}`, '1');
        closeAuthPromptModal();
        signIn();
    };

    const handleCloseModal = () => {
        PROMPTS[promptType].tracking.trackClose(journey);
        sessionStorage.setItem(`authPromptClose_${experimentName}`, '1');
        closeAuthPromptModal();
    };

    return isFeatureEnabled ? (
        <ModalDrawer isShowing={isShowingAuthPrompt} close={handleCloseModal}>
            <PromptContent>
                <PromptHeading size="5" data-testid="auth-prompt-title">
                    {PROMPTS[promptType].title}
                </PromptHeading>
                <SignInButton onClick={handleSignInClick}>Sign in</SignInButton>
                <RegisterButton
                    variant="tertiary"
                    onClick={handleRegisterClick}
                >
                    No account? Register
                </RegisterButton>
            </PromptContent>
        </ModalDrawer>
    ) : null;
};

export default withOptimizely(AuthPrompts);
